<template>
	<div>
		<div class="flex xs-12 xs-between mt6">
			<div class="fs4 lh125 fw7 c1 mb2">{{ $t("facts") }}</div>
			<j-button
				type="tertiary"
				v-if="hasActions"
				@click="handleAdd()"
				v-can:or="['writeIndividualFacts', 'writeCompanyFacts']"
			>
				<crm-icon name="add" color="c2" size="small" />
				{{ $t("add") }}
			</j-button>
		</div>

		<!-- Body -->
		<div v-if="!inlineAdd && !inlineUpdate">
			<j-expendable-box
				v-if="facts.length > 0"
				class="mb2 bg5 b1 r2 bc7 fs3 c6"
			>
				<div slot="header" class="pl3 c2 fw7">
					<p>{{ `${$t("facts_count")}: ${facts.length}` }}</p>
				</div>
				<div slot="body" v-for="(item, i) in facts" :key="i" class="bt1 bc7">
					<div class="flex xs-12 p3">
						<!-- Label -->
						<div class="flex xs-6 xs-left xs-middle fw7 c6">
							{{ item.label | unCamelCase }}
						</div>
						<!-- Body -->
						<div class="flex xs-3 xs-left xs-middle" v-if="item.value">
							<!-- If array -->
							<ul class="pl5" v-if="item.value.arrayValue">
								<li v-for="(listItem, j) in item.value.arrayValue" :key="j">
									{{ listItem.stringValue }}
								</li>
							</ul>

							<!-- If object -->
							<div v-else-if="item.value.objectValue">
								<span class="cu2 c2 fw7" @click="onPreview(item.value)"
									>See Object Value</span
								>
							</div>

							<!-- Else -->
							<div v-else>{{ parseFactValue(item.value) }}</div>
						</div>

						<!-- Actions -->
						<div v-if="hasActions" class="flex xs-3 xs-right xs-middle lh125">
							<crm-icon
								v-can:or="['writeIndividualFacts', 'writeCompanyFacts']"
								name="edit"
								size="normal"
								color="c6 ml4"
								@click.native="handleUpdate(item)"
							/>
							<crm-icon
								v-can:or="['writeIndividualFacts', 'writeCompanyFacts']"
								name="delete"
								size="normal"
								color="cError"
								@click.native="handleDelete(item.id)"
							/>
						</div>
					</div>
				</div>
			</j-expendable-box>

			<!-- Empty -->
			<j-card v-if="facts.length === 0" class="mb2 bg5 p2 ta2 fs3 c1">
				{{ $t("errors.no_data") }}
			</j-card>
		</div>

		<!-- Add Modal -->
		<core-modal :name="`addFact${id}`" :headline="$t('add_fact')">
			<entity-add
				:id="id"
				for="facts"
				:config="addConfig"
				@added="afterAdded"
			/>
		</core-modal>

		<!-- Update Modal -->
		<core-modal
			:name="`updateFact${id}`"
			:headline="$t('update_fact')"
			@close="afterUpdated"
		>
			<entity-update
				:id="id"
				for="facts"
				:updateId="updateId"
				:config="updateConfig"
				@updated="afterUpdated"
			/>
		</core-modal>

		<!-- Preview Modal -->
		<core-modal :name="`previewValue${id}`" :headline="$t('preview')">
			<div class="p4">
				<pre>
				{{ valueToPreview }}
				</pre
				>
			</div>
		</core-modal>

		<!-- Inline Add -->
		<div v-if="inlineAdd" class="bg4 r2 bc7 b1">
			<div class="flex xs-between p2 xs-12">
				<div class="fw7 fs3 c1">{{ $t("add_fact") }}</div>
				<crm-icon
					size="small"
					color="c1"
					name="cross"
					class="cu2"
					@click.native="inlineAdd = false"
				/>
			</div>
			<entity-add
				:id="id"
				size="small"
				fsLabel="fs3"
				for="facts"
				:config="addConfig"
				@added="afterAdded"
			/>
		</div>

		<!-- Inline Update -->
		<div v-if="inlineUpdate" class="bg4 r2 bc7 b1">
			<div class="flex xs-between p2 xs-12">
				<div class="fw7 fs3 c1">{{ $t("update_fact") }}</div>
				<crm-icon
					size="small"
					color="c1"
					name="cross"
					class="cu2"
					@click.native="inlineUpdate = false"
				/>
			</div>
			<entity-update
				:id="id"
				size="small"
				fsLabel="fs3"
				for="facts"
				:updateId="updateId"
				:config="updateConfig"
				@updated="afterUpdated"
			/>
		</div>
	</div>
</template>

<script>
import JCard from "@/components/JCard/"
import JList from "@/components/JList/"
import JExpendableBox from "@/components/JExpendableBox/"
import JButton from "@/components/JButton/"
import CrmIcon from "@/components/CrmIcon/"
import CoreModal from "@/modules/core/components/CoreModal"
import EntityAdd from "./EntityAdd"
import EntityUpdate from "./EntityUpdate"
import api from "@/modules/core/api/"
import dayjs from "dayjs"
import get from "lodash.get"

export default {
	props: {
		id: String,
		facts: Array,
		hasActions: Boolean,
		mode: String
	},

	components: {
		JCard,
		JList,
		JExpendableBox,
		JButton,
		CrmIcon,
		CoreModal,
		EntityAdd,
		EntityUpdate
	},

	data() {
		return {
			inlineAdd: false,
			inlineUpdate: false,
			addConfig: [
				{
					componentName: "j-base-select",
					id: "factOptions",
					label: this.$t("fact_type"),
					options: [
						{ name: "Text", value: "stringValue" },
						{ name: "Number", value: "numberValue" },
						{ name: "Boolean", value: "booleanValue" },
						{ name: "Date", value: "dateValue" },
						{ name: "List", value: "arrayValue" }
					],
					validate: "required"
				},
				{
					componentName: "j-base-input-text",
					id: "type",
					label: this.$t("fact_label"),
					validate: "required"
				},
				{
					componentName: "j-base-input-text",
					showFieldWhen: {
						targetId: "factOptions",
						targetValue: "stringValue"
					},
					id: "stringValue",
					label: this.$t("value"),
					validate: "required"
				},
				{
					componentName: "j-base-input-number",
					showFieldWhen: {
						targetId: "factOptions",
						targetValue: "numberValue"
					},
					id: "numberValue",
					label: this.$t("value"),
					description: "(Integer or Decimal)",
					validate: "required"
				},
				{
					componentName: "j-base-toggle",
					showFieldWhen: {
						targetId: "factOptions",
						targetValue: "booleanValue"
					},
					id: "booleanValue",
					label: this.$t("value"),
					validate: "required"
				},
				{
					componentName: "j-base-input-text",
					showFieldWhen: {
						targetId: "factOptions",
						targetValue: "dateValue"
					},
					id: "dateValue",
					label: this.$t("value"),
					description: "(DD/MM/YYYY)",
					validate: "date_format:DD/MM/YYYY|required"
				},
				{
					componentName: "j-input-list",
					showFieldWhen: {
						targetId: "factOptions",
						targetValue: "arrayValue"
					},
					id: "arrayValue",
					label: this.$t("value"),
					validate: "required"
				}
			],

			updateId: null,

			updateConfig: null,

			// When there is a complex value a we need to display a preview modal for it
			valueToPreview: null
		}
	},

	methods: {
		onPreview(valueToPreview) {
			// Check if value is object
			if (valueToPreview.objectValue !== null) {
				try {
					this.valueToPreview = JSON.stringify(
						valueToPreview,
						(key, value) => {
							// Remove nulls
							if (value !== null) return value
						},
						2
					)
				} catch (e) {
					console.error("Error stringifying objectValue on entity card", e)
				}
			}

			this.$modal.show(`previewValue${this.id}`)
		},

		parseFactValue(value) {
			if (!value || value.arrayValue) return null
			if (value.stringValue) return value.stringValue
			if (value.numberValue) return value.numberValue
			if (value.dateValue) return dayjs(value.dateValue).format("DD/MM/YYYY")
			if (value.booleanValue === true) return this.$t("yes")
			if (value.booleanValue !== true) return this.$t("no")
		},

		async handleDelete(payload) {
			const res = await api.deleteFact({
				entityId: this.id,
				id: payload
			})

			// Error
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
			}

			this.$emit("deleted")
		},

		handleAdd() {
			if (this.mode === "modal") this.$modal.show(`addFact${this.id}`)
			if (this.mode === "inline") {
				this.inlineAdd = !this.inlineAdd
				this.inlineUpdate = false
			}
		},

		handleUpdate(payload) {
			// Set updateId which is id of contact, address, identities, fact etc
			this.updateId = payload.id

			// get selected fact option by checking which field is a non null
			const selectedFactOption = Object.entries(payload.value)
				.filter(item => item[1] !== null)
				.map(item => item[0])
				.toString()

			// Set updateConfig
			this.updateConfig = [
				{
					componentName: "j-base-select",
					id: "factOptions",
					label: this.$t("fact_type"),
					value: selectedFactOption,
					options: [
						{ name: "Text", value: "stringValue" },
						{ name: "Number", value: "numberValue" },
						{ name: "Boolean", value: "booleanValue" },
						{ name: "Date", value: "dateValue" },
						{ name: "List", value: "arrayValue" }
					],
					validate: "required"
				},
				{
					componentName: "j-base-input-text",
					id: "type",
					value: payload.label,
					label: this.$t("fact_label"),
					validate: "required"
				},
				{
					componentName: "j-base-input-text",
					showFieldWhen: {
						targetId: "factOptions",
						targetValue: "stringValue"
					},
					id: "stringValue",
					label: this.$t("value"),
					validate: "required",
					value: payload.value.stringValue ? payload.value.stringValue : null
				},
				{
					componentName: "j-base-input-number",
					showFieldWhen: {
						targetId: "factOptions",
						targetValue: "numberValue"
					},
					id: "numberValue",
					label: this.$t("value"),
					description: "(Integer or Decimal)",
					validate: "required",
					value: payload.value.numberValue ? payload.value.numberValue : null
				},
				{
					componentName: "j-base-toggle",
					showFieldWhen: {
						targetId: "factOptions",
						targetValue: "booleanValue"
					},
					id: "booleanValue",
					label: this.$t("value"),
					validate: "required",
					value: payload.value.booleanValue ? payload.value.booleanValue : false
				},
				{
					componentName: "j-base-input-text",
					showFieldWhen: {
						targetId: "factOptions",
						targetValue: "dateValue"
					},
					id: "dateValue",
					label: this.$t("value"),
					description: "(DD/MM/YYYY)",
					validate: "date_format:DD/MM/YYYY|required",
					value: payload.value.dateValue
						? dayjs(payload.value.dateValue).format("DD/MM/YYYY")
						: null
				},
				{
					componentName: "j-input-list",
					showFieldWhen: {
						targetId: "factOptions",
						targetValue: "arrayValue"
					},
					id: "arrayValue",
					label: this.$t("value"),
					validate: "required",
					value: payload.value.arrayValue ? payload.value.arrayValue : null
				}
			]

			// Show update modal
			if (this.mode === "modal") this.$modal.show(`updateFact${this.id}`)
			if (this.mode === "inline") this.inlineUpdate = true
		},

		afterAdded() {
			if (this.mode === "modal") this.$modal.hide(`addFact${this.id}`)
			if (this.mode === "inline") this.inlineAdd = false
			this.$emit("added")
		},

		afterUpdated() {
			if (this.mode === "modal") this.$modal.hide(`updateFact${this.id}`)
			if (this.mode === "inline") this.inlineUpdate = false
			this.$emit("updated")
		}
	},

	filters: {
		/**
		 * We are doing uncamelcasing all the facts types so that it's more human readible
		 * It's bit hacky, we should introduce dictionary for facts so that we can get name from API
		 */
		unCamelCase(val) {
			if (!val) return ""
			return val
				.replace(/([A-Z])/g, " $1") // insert a space before all caps
				.toLowerCase()
		}
	}
}
</script>
